import { get, has } from 'lodash';

import { type BuilderComponent } from 'hb-builder/types';
import { type CommonSchemaBuilder } from 'hb-builder/utils/migrate/schemas/types';

const getAttribute = (
  attributes: { [key: string]: any },
  path: string,
  {
    defaultValue,
    resolver,
  }: { defaultValue: number | null; resolver?: (v: string) => number | string },
) => {
  if (has(attributes, path)) {
    const value = get(attributes, path, defaultValue);

    if (value !== null && value !== undefined && resolver) {
      return resolver(value);
    }

    return value;
  }

  return defaultValue;
};

const attributes = {
  bodyBackgroundColor: {
    bodyBackgroundColor: (_, attributes) =>
      getAttribute(attributes, 'backgroundColor', {
        resolver: (v) => v.toLowerCase(),
        defaultValue: null,
      }),

    backgroundColor: () => null,
  },

  bodyWidth: {
    bodyWidth: (_, attributes) =>
      getAttribute(attributes, 'width', { defaultValue: 600 }),

    width: () => null,
  },

  containerBackgroundColor: {
    containerBackgroundColor: (_, attributes) =>
      getAttribute(attributes, 'backgroundColor', {
        resolver: (v) => v.toLowerCase(),
        defaultValue: null,
      }),

    backgroundColor: () => null,
  },

  containerPadding: {
    containerPadding: (_, attributes) => {
      const parsePadding = (top, right, bottom, left) =>
        `${parseInt(top || 0, 10)}px ${parseInt(right || 0, 10)}px ${parseInt(
          bottom || 0,
          10,
        )}px ${parseInt(left || 0, 10)}px`;

      const padding = get(attributes, 'padding');

      if (padding) {
        return parsePadding(padding, padding, padding, padding);
      }

      return parsePadding(
        get(attributes, 'paddingTop'),
        get(attributes, 'paddingRight'),
        get(attributes, 'paddingBottom'),
        get(attributes, 'paddingLeft'),
      );
    },

    padding: () => null,
    paddingTop: () => null,
    paddingRight: () => null,
    paddingBottom: () => null,
    paddingLeft: () => null,
  },

  horizontalAlign: {
    horizontalAlign: (_, attributes) =>
      getAttribute(attributes, 'align', {
        defaultValue: null,
      }),

    align: () => null,
  },

  textColor: {
    textColor: (_, attributes) =>
      getAttribute(attributes, 'color', {
        resolver: (v) => v.toLowerCase(),
        defaultValue: null,
      }),

    color: () => null,
  },

  textFontSize: {
    textFontSize: (_, attributes) =>
      getAttribute(attributes, 'fontSize', {
        resolver: (v) => parseInt(v, 10),
        defaultValue: null,
      }),

    fontSize: () => null,
  },

  textFontFamily: {
    textFontFamily: (_, attributes) =>
      getAttribute(attributes, 'fontFamily', {
        defaultValue: null,
      }),

    fontFamily: () => null,
  },

  imageSrc: {
    imageSrc: (_, attributes) =>
      getAttribute(attributes, 'src', {
        defaultValue: null,
      }),

    src: () => null,
  },

  imageWidth: {
    width: (value) => (/undefined/i.test(value) ? null : parseInt(value, 10)),
  },

  sectionWidth: {
    sectionWidth: (_, attributes) =>
      getAttribute(attributes, 'width', {
        resolver: (v) => (v === 'small' ? 600 : 1190),
        defaultValue: 600,
      }),

    width: () => null,
  },
};

const schema = ({ document }: CommonSchemaBuilder<BuilderComponent>) => {
  if (document?.type === 'Document') {
    return {
      Document: {
        content: () => null,
        type: () => 'Root',
      },

      Body: {
        attributes: () => ({
          ...attributes.bodyBackgroundColor,
          ...attributes.bodyWidth,
        }),
        content: () => null,
      },

      Section: {
        attributes: () => ({
          ...attributes.containerBackgroundColor,
          ...attributes.containerPadding,
          ...attributes.sectionWidth,
        }),
        content: () => null,
      },

      Column: {
        attributes: () => ({
          ...attributes.containerBackgroundColor,
          ...attributes.containerPadding,
        }),
        content: () => null,
      },

      Text: {
        attributes: () => ({
          ...attributes.containerBackgroundColor,
          ...attributes.containerPadding,
          ...attributes.horizontalAlign,
          ...attributes.textColor,
          ...attributes.textFontFamily,
          ...attributes.textFontSize,
        }),
        children: () => null,
      },

      Image: {
        attributes: () => ({
          ...attributes.containerBackgroundColor,
          ...attributes.containerPadding,
          ...attributes.horizontalAlign,
          ...attributes.imageSrc,
          ...attributes.imageWidth,
        }),
        children: () => null,
      },
    };
  }

  return null;
};

export default schema;
